import type { ReactNode } from 'react';
import clsx from 'clsx';

import styles from './DataCard.module.css';

export type Props = {
  /**
   * Singles this card out from its siblings as featured, selected or recommended, with the resting
   * shadow and nothing else.
   *
   * A *minority-of-the-set* state: highlight every card in a grid and nothing is highlighted, so the
   * shadows are decoration again, which is the one thing the surface rules do not allow.
   */
  highlighted?: boolean;
  children: ReactNode;
};

/**
 * A content surface: white, separated from the white page by its border, never by a shadow or a
 * tint. Sections, panels, stat tiles, anything the reader looks *at*.
 *
 * The default of the three card variants in `design/50-components.md` "Card". Reach for
 * `ActionCard` when pressing the whole card launches something and `EntityCard` when it navigates
 * to a thing in the product, and for none of the three when the block is simply a region of a page:
 * a card is a surface a reader can tell from the page, so a page built entirely out of them has no
 * surfaces at all.
 *
 * It sets padding, border, radius and background and nothing else. The rhythm *inside* it belongs to
 * the caller's own `Stack`, off the depth ladder: `lg` between two groups, `md` between a title and
 * the body text under it, and never `xs`, which is the icon-to-text step at the bottom of that
 * ladder and glues a title to its own summary (PLT-6025).
 */
export function DataCard(props: Props) {
  const { highlighted = false, children } = props;
  return <div className={clsx(styles.root, highlighted && styles.highlighted)}>{children}</div>;
}
